/**
 *  SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 *
 * @param {*} title Page title
 * @param {*} description Meta description
 * @param {*} location Page Location object
 * @param {*} meta Array of optional meta data objects
 * @param {*} ldJson JSON-LD schema object
 * @param {*} notLocalized Flag for pages that have 1 language and don't need both hreflang tags
 * @returns
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'react-intl';
import { useLocation } from '@reach/router';
import { isProdEnv } from '@pcf-engineering/pcs-ui-kit/utils';
import { isKiosk } from '../../utils/isKiosk';

interface Props {
  title?: string;
  description?: string;
  ldJson?: Record<string, unknown>;
  notLocalized?: boolean;
}

const Seo = ({ title, description, ldJson, notLocalized }: Props) => {
  const { locale } = useIntl();
  const { pathname: pathName } = useLocation();
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
          title
          description
          author
        }
        buildTime
      }
    }
  `);
  const lang = locale ?? 'en';
  const metaDescription = description ?? site.siteMetadata.description;
  const otherLang = lang === 'en' ? 'fr' : 'en';
  const otherPath = pathName
    ? pathName.replace(`/${lang}/`, `/${otherLang}/`)
    : '';
  const isKioskMode = isKiosk();
  return (
    <Helmet
      prioritizeSeoTags
      defer={false}
      htmlAttributes={{
        lang,
      }}
      defaultTitle={title}
    >
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={metaDescription} />
      <meta name="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={site.siteMetadata.author} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="build-date" content={site.buildTime} />
      {(isKioskMode || !isProdEnv(process.env.GATSBY_DEPLOY_ENV)) && <meta name="robots" content="noindex" />}
      <link rel="canonical" href={`${site.siteMetadata.siteUrl}${pathName}`} />
      <link rel="stylesheet" href="https://use.typekit.net/tgs5vqy.css" async />
      <link
        rel="alternate"
        href={`${site.siteMetadata.siteUrl}${pathName}`}
        hrefLang={`${lang}-CA`}
        data-testid="seo-link-en"
      />
      {!notLocalized && (
        <link
          rel="alternate"
          href={`${site.siteMetadata.siteUrl}${otherPath}`}
          hrefLang={`${otherLang}-CA`}
          data-testid="seo-link-fr"
        />
      )}
      {ldJson && (
        <script type="application/ld+json" data-testid="seo-script">
          {JSON.stringify(ldJson)}
        </script>
      )}
    </Helmet>
  );
};

export default Seo;
